import React from 'react';
import { X, Check, ArrowRight, AlertTriangle, BatteryWarning, HeartHandshake } from 'lucide-react';
interface SectionProps {
  onScrollToPrice: () => void;
}
export function ProblemSection({
  onScrollToPrice
}: SectionProps) {
  return <section className="py-16 bg-white px-4" data-id="element-51">
      <div className="max-w-3xl mx-auto" data-id="element-52">
        <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8" data-id="element-53">
          Si sos madre o padre, dejame hacerte una pregunta honesta...
        </h3>

        <div className="bg-red-50 border-l-4 border-red-500 p-6 md:p-8 rounded-r-lg mb-12" data-id="element-54">
          <p className="text-xl md:text-2xl font-bold text-red-900 italic text-center" data-id="element-55">
            "¿Sentís que el celular está más presente en la vida de tus hijos
            que vos?"
          </p>
        </div>

        <div className="space-y-4 text-lg text-gray-700 mb-12" data-id="element-56">
          <p data-id="element-57">No porque seas mal padre o mala madre.</p>
          <p data-id="element-58">No porque no te importe tu familia.</p>
          <p className="font-bold text-gray-900" data-id="element-59">
            Sino porque nadie nos enseñó a criar hijos en un mundo dominado por
            pantallas.
          </p>
        </div>

        <div className="bg-gray-50 p-8 rounded-2xl shadow-inner mb-12" data-id="element-60">
          <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-61">
            <BatteryWarning className="text-red-500" data-id="element-62" />
            El desgaste diario que casi nadie dice en voz alta
          </h4>
          <ul className="space-y-4" data-id="element-63">
            {['Pedís que apaguen el celular y hay enojo', 'Quitás la pantalla y aparecen berrinches', 'Intentás poner límites y sentís culpa', 'Querés conectar, pero no sabés cómo', 'Terminás cansado/a, frustrado/a y dudando'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-64">
                <X className="w-6 h-6 text-red-500 shrink-0" data-id="element-65" />
                <span className="text-gray-700" data-id="element-66">{item}</span>
              </li>)}
          </ul>
        </div>

        <div className="text-center bg-slate-900 text-white p-8 rounded-2xl shadow-xl" data-id="element-67">
          <p className="text-lg mb-2 opacity-80" data-id="element-68">
            Y aparece esa sensación silenciosa de:
          </p>
          <p className="text-2xl font-bold text-yellow-400 italic mb-6" data-id="element-69">
            “Algo se me está escapando… y no sé cómo frenarlo.”
          </p>
          <p className="text-xl font-medium" data-id="element-70">Criar no debería sentirse así.</p>
        </div>
      </div>
    </section>;
}
export function RealProblemSection() {
  return <section className="py-16 bg-gray-100 px-4" data-id="element-71">
      <div className="max-w-3xl mx-auto text-center" data-id="element-72">
        <h3 className="text-3xl font-black text-gray-900 mb-8 uppercase" data-id="element-73">
          El problema real{' '}
          <span className="text-red-600" data-id="element-74">(no es el celular)</span>
        </h3>

        <p className="text-xl text-gray-700 mb-8" data-id="element-75">
          El celular no es solo un dispositivo. Es un{' '}
          <span className="font-bold" data-id="element-76">educador silencioso</span> que:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-10" data-id="element-77">
          {['Distorsiona valores', 'Reduce la empatía', 'Afecta la conducta', 'Genera dependencia emocional', 'Reemplaza la presencia adulta'].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg shadow-sm flex items-center gap-3 border border-gray-200" data-id="element-78">
              <AlertTriangle className="text-yellow-500 w-5 h-5" data-id="element-79" />
              <span className="font-medium text-gray-800" data-id="element-80">{item}</span>
            </div>)}
        </div>

        <div className="bg-white p-6 rounded-xl border-2 border-red-100 shadow-lg inline-block" data-id="element-81">
          <p className="text-2xl font-bold text-gray-900" data-id="element-82">
            📱 Se convirtió en el nuevo “niñero”,
          </p>
          <p className="text-lg text-gray-600 mt-2" data-id="element-83">
            pero uno que no enseña límites, respeto ni autocontrol.
          </p>
        </div>
      </div>
    </section>;
}
export function SolutionSection({
  onScrollToPrice
}: SectionProps) {
  return <section className="py-16 bg-white px-4" data-id="element-84">
      <div className="max-w-4xl mx-auto" data-id="element-85">
        <div className="text-center mb-12" data-id="element-86">
          <span className="bg-green-100 text-green-700 font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-87">
            ✅ La Solución Definitiva
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6" data-id="element-88">
            CRIANZA SIN CELULAR
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-89">
            Un programa completo en{' '}
            <span className="font-bold text-gray-900" data-id="element-90">5 TOMOS + 3 BONOS</span>,
            diseñado para ayudarte a recuperar tu rol como guía, sin gritar, sin
            castigar y sin romper el vínculo.
          </p>
        </div>

        {/* Process Steps */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-16" data-id="element-91">
          <div className="text-center" data-id="element-92">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" data-id="element-93">
              📂
            </div>
            <p className="font-bold" data-id="element-94">Accedés al contenido</p>
          </div>
          <ArrowRight className="hidden md:block text-gray-300 w-8 h-8" data-id="element-95" />
          <div className="text-center" data-id="element-96">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" data-id="element-97">
              👆
            </div>
            <p className="font-bold" data-id="element-98">Aplicás paso a paso</p>
          </div>
          <ArrowRight className="hidden md:block text-gray-300 w-8 h-8" data-id="element-99" />
          <div className="text-center" data-id="element-100">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" data-id="element-101">
              👨‍👩‍👧‍👦
            </div>
            <p className="font-bold" data-id="element-102">Transformás la dinámica</p>
          </div>
        </div>

        <div className="bg-blue-50 p-8 rounded-2xl text-center mb-12" data-id="element-103">
          <p className="text-xl font-medium text-blue-900" data-id="element-104">
            Sin improvisar. Sin pelear todos los días. Sin sentirte solo/a.
          </p>
        </div>

        <button onClick={onScrollToPrice} className="w-full md:w-auto block mx-auto bg-red-600 hover:bg-red-500 text-white text-xl font-bold py-4 px-12 rounded-full shadow-lg hover:shadow-xl transition-all" data-id="element-105">
          QUIERO TRANSFORMAR MI FAMILIA HOY
        </button>
      </div>
    </section>;
}
export function ModulesSection() {
  const modules = [{
    title: 'TOMO 1 – El enemigo silencioso en casa',
    desc: 'Por qué el celular está criando a tus hijos y cómo recuperar el control.',
    color: 'bg-red-100 text-red-800'
  }, {
    title: 'TOMO 2 – El daño invisible',
    desc: 'Cómo el uso excesivo afecta emociones, conducta y vínculo familiar.',
    color: 'bg-blue-100 text-blue-800'
  }, {
    title: 'TOMO 3 – Límites sin gritos ni culpa',
    desc: 'Cómo quitar el celular sin provocar guerras en casa.',
    color: 'bg-green-100 text-green-800'
  }, {
    title: 'TOMO 4 – Reemplazar la pantalla',
    desc: 'Actividades reales que vuelven a enganchar a tus hijos con la vida.',
    color: 'bg-orange-100 text-orange-800'
  }, {
    title: 'TOMO 5 – La familia que sí se puede',
    desc: 'Valores, hábitos y conexión que sostienen el cambio en el tiempo.',
    color: 'bg-purple-100 text-purple-800'
  }];
  return <section className="py-16 bg-white px-4 border-t border-gray-100" data-id="element-106">
      <div className="max-w-4xl mx-auto" data-id="element-107">
        <h3 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-108">
          📘 ¿QUÉ INCLUYE EXACTAMENTE?
        </h3>

        <div className="space-y-6" data-id="element-109">
          {modules.map((mod, i) => <div key={i} className="flex flex-col md:flex-row gap-6 p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow" data-id="element-110">
              <div className={`w-full md:w-auto md:min-w-[120px] h-auto md:h-full flex items-center justify-center rounded-lg p-4 ${mod.color} font-bold text-center`} data-id="element-111">
                TOMO {i + 1}
              </div>
              <div data-id="element-112">
                <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-113">
                  {mod.title}
                </h4>
                <p className="text-gray-600" data-id="element-114">{mod.desc}</p>
              </div>
            </div>)}
        </div>

        <p className="text-center text-gray-500 mt-8 italic" data-id="element-115">
          Todo organizado, claro y aplicable a la vida real.
        </p>
      </div>
    </section>;
}
export function ComparisonSection() {
  return <section className="py-16 bg-gray-900 text-white px-4" data-id="element-116">
      <div className="max-w-5xl mx-auto" data-id="element-117">
        <h3 className="text-3xl font-bold text-center mb-12" data-id="element-118">
          🔄 ANTES vs DESPUÉS
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-119">
          {/* Before */}
          <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700" data-id="element-120">
            <h4 className="text-2xl font-bold text-red-400 mb-6 flex items-center gap-2" data-id="element-121">
              <X className="w-8 h-8" data-id="element-122" /> ANTES
            </h4>
            <ul className="space-y-4" data-id="element-123">
              {['Peleas constantes por el celular', 'Silencio en la mesa', 'Hijos irritables y desconectados', 'Padres cansados y frustrados', 'Culpa al poner límites'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300" data-id="element-124">
                  <X className="w-5 h-5 text-red-500 shrink-0 mt-1" data-id="element-125" />
                  <span data-id="element-126">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* After */}
          <div className="bg-white text-gray-900 p-8 rounded-2xl shadow-2xl transform md:scale-105 border-4 border-green-500 relative" data-id="element-127">
            <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-128">
              RESULTADO
            </div>
            <h4 className="text-2xl font-bold text-green-600 mb-6 flex items-center gap-2" data-id="element-129">
              <Check className="w-8 h-8" data-id="element-130" /> DESPUÉS
            </h4>
            <ul className="space-y-4" data-id="element-131">
              {['Límites claros y respetados', 'Conversaciones reales', 'Más calma en casa', 'Hijos más seguros y presentes', 'Familia más unida'].map((item, i) => <li key={i} className="flex items-start gap-3 font-medium" data-id="element-132">
                  <Check className="w-5 h-5 text-green-600 shrink-0 mt-1" data-id="element-133" />
                  <span data-id="element-134">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}